/**
 * Advanced Table Parser
 * Intelligently parses various table formats from plain text
 */

/**
 * Main table parser - detects and parses various table formats
 */
export const parseTable = (content) => {
  if (!content || typeof content !== 'string') return null;

  // Try different parsing strategies
  const parsers = [
    parseBoxTable,         // ASCII box tables with +---+ borders
    parseMarkdownTable,
    parsePipeTable,
    parseWarehouseTable,  // New parser for warehouse-style tables
    parseAlignedTable,
    parseTabTable,
    parseKeyValueTable,
    parseListTable
  ];

  for (const parser of parsers) {
    const result = parser(content);
    if (result && result.rows && result.rows.length > 0) {
      return enrichTableData(result);
    }
  }

  return null;
};

/**
 * Parse ASCII box tables with +---+ borders
 * Handles patterns like:
 * +-------+-------+
 * | Col 1 | Col 2 |
 * +-------+-------+
 * | Data  | Data  |
 * +-------+-------+
 */
const parseBoxTable = (content) => {
  const lines = content.split('\n').map(l => l.trim());

  // Check if this looks like a box table (has +---+ or +===+ patterns)
  // More flexible pattern: starts with +, ends with +, has - or = in between
  const hasBorders = lines.some(l => /^\+[-=+\s]+\+$/.test(l.trim()));
  if (!hasBorders) return null;

  const rows = [];
  let headers = null;

  for (const line of lines) {
    if (!line.trim()) continue;

    // Skip border lines (+---+, +=====+, +---+---+, etc.)
    // Pattern: + followed by any combination of -, =, +, spaces, ending with +
    if (/^\+[-=+\s]+\+$/.test(line)) {
      continue;
    }

    // Process data lines (|...|)
    // Must start and end with |, and have content
    if (line.startsWith('|') && line.endsWith('|') && line.length > 2) {
      // Split by | and clean up
      const cells = line
        .split('|')
        .slice(1, -1)  // Remove first and last empty strings from split
        .map(c => c.trim());

      // Skip if no cells or if it's a separator row (all cells are just dashes/equals/spaces)
      if (cells.length === 0 || cells.every(c => /^[-=+\s]*$/.test(c))) {
        continue;
      }

      if (!headers) {
        headers = cells;
      } else {
        rows.push(cells);
      }
    }
  }

  // Validate we found a proper table
  if (!headers || rows.length === 0) return null;

  // Ensure consistent column count
  const headerCount = headers.length;
  const validRows = rows.filter(r => r.length === headerCount);

  if (validRows.length === 0) return null;

  return { headers, rows: validRows, type: 'box' };
};

/**
 * Parse standard markdown tables with pipes
 */
const parseMarkdownTable = (content) => {
  const lines = content.split('\n').map(l => l.trim()).filter(Boolean);
  const pipeLines = lines.filter(l => l.includes('|'));
  
  if (pipeLines.length < 2) return null;

  const rows = [];
  let headers = null;
  let alignments = [];

  for (const line of pipeLines) {
    // Skip separator lines
    if (/^[\s|:-]+$/.test(line)) {
      // Extract alignment info
      const aligns = line.split('|').map(cell => {
        const trimmed = cell.trim();
        if (trimmed.startsWith(':') && trimmed.endsWith(':')) return 'center';
        if (trimmed.endsWith(':')) return 'right';
        return 'left';
      });
      alignments = aligns;
      continue;
    }

    const cells = line
      .split('|')
      .map(c => c.trim())
      .filter((c, i, arr) => i !== 0 && i !== arr.length - 1 || c); // Remove empty first/last

    if (!headers) {
      headers = cells;
    } else {
      rows.push(cells);
    }
  }

  return { headers, rows, alignments, type: 'markdown' };
};

/**
 * Parse simple pipe-separated tables
 */
const parsePipeTable = (content) => {
  const lines = content.split('\n').map(l => l.trim()).filter(Boolean);
  const pipeLines = lines.filter(l => l.includes('|'));
  
  if (pipeLines.length < 2) return null;

  const rows = pipeLines.map(line => 
    line.split('|').map(c => c.trim()).filter(Boolean)
  );

  // Check for consistent column count (be more lenient)
  const colCounts = rows.map(r => r.length);
  const avgCols = Math.round(colCounts.reduce((a, b) => a + b, 0) / colCounts.length);
  
  // Allow more variation in column counts
  if (Math.max(...colCounts) - Math.min(...colCounts) > 3) return null;

  return {
    headers: rows[0],
    rows: rows.slice(1),
    type: 'pipe'
  };
};

/**
 * Parse warehouse-style tables with arrow notation
 */
const parseWarehouseTable = (content) => {
  const lines = content.split('\n').map(l => l.trim()).filter(Boolean);
  
  if (lines.length < 2) return null;
  
  // Check if this looks like a warehouse table (has arrows or specific patterns)
  const hasArrows = lines.some(l => l.includes('→') || l.includes('->'));
  const hasQuotes = lines.some(l => l.includes("'"));
  
  if (!hasArrows && !hasQuotes) return null;
  
  // Parse header line (might have various formats)
  const headerLine = lines[0];
  let headers = [];
  
  // Try to extract headers from first line
  if (headerLine.match(/[A-Z]{2,}/g)) {
    headers = headerLine.match(/[A-Z][A-Z\s]+/g)?.map(h => h.trim()) || [];
  } else {
    // Split by multiple spaces
    headers = headerLine.split(/\s{2,}/).filter(Boolean);
  }
  
  // Parse data rows
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim() || line.startsWith('-')) continue;
    
    // Parse each row - handle quotes and arrows specially
    const cells = [];
    let current = '';
    let inQuotes = false;
    
    for (let j = 0; j < line.length; j++) {
      const char = line[j];
      const nextChar = line[j + 1];
      
      if (char === "'" || char === '`') {
        inQuotes = !inQuotes;
        current += char;
      } else if (!inQuotes && (char === ' ' && nextChar === ' ')) {
        // Double space marks cell boundary
        if (current.trim()) {
          cells.push(current.trim());
          current = '';
        }
        // Skip extra spaces
        while (line[j + 1] === ' ') j++;
      } else {
        current += char;
      }
    }
    
    // Add last cell
    if (current.trim()) {
      cells.push(current.trim());
    }
    
    if (cells.length > 0) {
      rows.push(cells);
    }
  }
  
  // Ensure consistent column count
  const maxCols = Math.max(headers.length, ...rows.map(r => r.length));
  
  // Pad headers if needed
  while (headers.length < maxCols) {
    headers.push(`Column ${headers.length + 1}`);
  }
  
  // Pad rows if needed
  rows.forEach(row => {
    while (row.length < maxCols) {
      row.push('');
    }
  });
  
  return {
    headers,
    rows,
    type: 'warehouse'
  };
};

/**
 * Parse space-aligned tables (2+ spaces as separator)
 */
const parseAlignedTable = (content) => {
  const lines = content.split('\n').map(l => l.trimEnd()).filter(l => l.trim());
  
  if (lines.length < 2) return null;

  // Detect column positions based on consistent spacing
  const columnPositions = detectColumnPositions(lines);
  
  // If we can't detect columns well, try a simpler approach
  if (columnPositions.length < 2) {
    // Fallback to splitting by multiple spaces
    const rows = lines.map(line => line.split(/\s{2,}/).filter(Boolean));
    if (rows.length > 1) {
      return {
        headers: rows[0],
        rows: rows.slice(1),
        type: 'aligned-fallback'
      };
    }
    return null;
  }

  const rows = lines.map(line => extractColumnsAtPositions(line, columnPositions));
  
  // Filter out separator lines
  const dataRows = rows.filter(row => 
    !row.every(cell => /^[-=+]+$/.test(cell.trim()))
  );

  return {
    headers: dataRows[0],
    rows: dataRows.slice(1),
    type: 'aligned'
  };
};

/**
 * Detect column positions in aligned text
 */
const detectColumnPositions = (lines) => {
  // Find positions where there are consistent gaps
  const gaps = [];
  
  lines.forEach(line => {
    let inGap = false;
    let gapStart = -1;
    
    for (let i = 0; i < line.length; i++) {
      if (line[i] === ' ' && line[i + 1] === ' ') {
        if (!inGap) {
          gapStart = i;
          inGap = true;
        }
      } else if (inGap) {
        gaps.push({ start: gapStart, end: i });
        inGap = false;
      }
    }
  });

  // Find consistent gap positions across lines
  const positionCounts = {};
  gaps.forEach(gap => {
    const key = `${gap.start}`;
    positionCounts[key] = (positionCounts[key] || 0) + 1;
  });

  // Get positions that appear in most lines
  const threshold = lines.length * 0.6;
  const positions = Object.entries(positionCounts)
    .filter(([_, count]) => count >= threshold)
    .map(([pos, _]) => parseInt(pos))
    .sort((a, b) => a - b);

  // Add start and end positions
  return [0, ...positions, Infinity];
};

/**
 * Extract columns at specific positions
 */
const extractColumnsAtPositions = (line, positions) => {
  const columns = [];
  
  for (let i = 0; i < positions.length - 1; i++) {
    const start = positions[i];
    const end = positions[i + 1];
    const column = line.substring(start, Math.min(end, line.length)).trim();
    columns.push(column);
  }
  
  return columns;
};

/**
 * Parse tab-separated tables
 */
const parseTabTable = (content) => {
  const lines = content.split('\n').map(l => l.trim()).filter(Boolean);
  const tabLines = lines.filter(l => l.includes('\t'));
  
  if (tabLines.length < 2) return null;

  const rows = tabLines.map(line => line.split('\t').map(c => c.trim()));
  
  return {
    headers: rows[0],
    rows: rows.slice(1),
    type: 'tab'
  };
};

/**
 * Parse key-value pairs as a table
 */
const parseKeyValueTable = (content) => {
  const lines = content.split('\n').map(l => l.trim()).filter(Boolean);
  const kvPattern = /^([^:]+):\s*(.+)$/;
  
  const kvPairs = lines
    .map(line => {
      const match = line.match(kvPattern);
      return match ? [match[1].trim(), match[2].trim()] : null;
    })
    .filter(Boolean);

  if (kvPairs.length < 2) return null;

  return {
    headers: ['Property', 'Value'],
    rows: kvPairs,
    type: 'keyvalue'
  };
};

/**
 * Parse structured lists as tables
 */
const parseListTable = (content) => {
  const lines = content.split('\n').filter(l => l.trim());
  const listPattern = /^[-*•]\s+([^:]+):\s*(.+)$/;
  
  const items = lines
    .map(line => {
      const match = line.match(listPattern);
      return match ? [match[1].trim(), match[2].trim()] : null;
    })
    .filter(Boolean);

  if (items.length < 2) return null;

  // Detect if it's a consistent structure
  const keys = [...new Set(items.map(item => item[0]))];
  if (keys.length === items.length) {
    // Each key is unique - treat as key-value
    return {
      headers: ['Item', 'Details'],
      rows: items,
      type: 'list'
    };
  }

  return null;
};

/**
 * Enrich table data with metadata and formatting hints
 */
const enrichTableData = (table) => {
  if (!table || !table.rows) return table;

  const enriched = { ...table };

  // Detect data types for each column
  enriched.columnTypes = detectColumnTypes(table);

  // Calculate column widths for rendering
  enriched.columnWidths = calculateColumnWidths(table);

  // Detect if table has numeric data that could be sorted
  enriched.sortable = enriched.columnTypes.some(t => 
    t === 'number' || t === 'date' || t === 'currency'
  );

  // Add row metadata
  enriched.rows = enriched.rows.map((row, index) => ({
    data: row,
    index,
    highlight: detectRowHighlight(row, enriched.columnTypes)
  }));

  return enriched;
};

/**
 * Detect data types for table columns
 */
const detectColumnTypes = (table) => {
  if (!table.headers) return [];

  const types = [];
  const numColumns = table.headers.length;

  for (let col = 0; col < numColumns; col++) {
    const values = table.rows.map(row => row[col] || '').filter(Boolean);
    types.push(detectDataType(values));
  }

  return types;
};

/**
 * Detect data type from column values
 */
const detectDataType = (values) => {
  const patterns = {
    number: /^-?\d+\.?\d*$/,
    currency: /^[$€£¥]\s*\d+\.?\d*$/,
    percentage: /^\d+\.?\d*\s*%$/,
    date: /^\d{1,4}[-/]\d{1,2}[-/]\d{1,4}$/,
    boolean: /^(yes|no|true|false|✓|✗|x)$/i,
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    url: /^https?:\/\//i
  };

  const typeCounts = {};

  values.forEach(value => {
    for (const [type, pattern] of Object.entries(patterns)) {
      if (pattern.test(value.trim())) {
        typeCounts[type] = (typeCounts[type] || 0) + 1;
      }
    }
  });

  // Find dominant type (>60% of values)
  const threshold = values.length * 0.6;
  for (const [type, count] of Object.entries(typeCounts)) {
    if (count >= threshold) return type;
  }

  return 'text';
};

/**
 * Calculate optimal column widths
 */
const calculateColumnWidths = (table) => {
  if (!table.headers) return [];

  const widths = table.headers.map(h => h.length);

  table.rows.forEach(row => {
    (Array.isArray(row) ? row : row.data || []).forEach((cell, i) => {
      if (cell && widths[i] !== undefined) {
        widths[i] = Math.max(widths[i], String(cell).length);
      }
    });
  });

  // Cap maximum width and calculate percentages
  const maxWidth = 50;
  const totalWidth = widths.reduce((a, b) => a + Math.min(b, maxWidth), 0);
  
  return widths.map(w => ({
    chars: Math.min(w, maxWidth),
    percentage: Math.round((Math.min(w, maxWidth) / totalWidth) * 100)
  }));
};

/**
 * Detect if a row should be highlighted based on content
 */
const detectRowHighlight = (row, columnTypes) => {
  const rowData = Array.isArray(row) ? row : row.data || [];
  
  // Check for warning/error indicators
  const warningPatterns = /warning|expired|failed|error|critical/i;
  const successPatterns = /success|completed|active|passed/i;

  const text = rowData.join(' ');
  
  if (warningPatterns.test(text)) return 'warning';
  if (successPatterns.test(text)) return 'success';

  // Check for outlier numeric values
  const numericColumns = columnTypes
    .map((type, index) => type === 'number' ? index : -1)
    .filter(i => i >= 0);

  for (const colIndex of numericColumns) {
    const value = parseFloat(rowData[colIndex]);
    if (!isNaN(value)) {
      if (value < 0) return 'negative';
      if (value > 1000000) return 'high';
    }
  }

  return null;
};

/**
 * Format table for display
 */
export const formatTableForDisplay = (table) => {
  if (!table) return '';

  const { headers, rows, columnWidths } = table;
  const formatted = [];

  // Add headers
  if (headers) {
    formatted.push(headers.map((h, i) => 
      padString(h, columnWidths[i]?.chars || 10)
    ).join(' | '));
    
    // Add separator
    formatted.push(headers.map((_, i) => 
      '-'.repeat(columnWidths[i]?.chars || 10)
    ).join('-|-'));
  }

  // Add rows
  const dataRows = rows.map(r => Array.isArray(r) ? r : r.data || []);
  dataRows.forEach(row => {
    formatted.push(row.map((cell, i) => 
      padString(cell || '', columnWidths[i]?.chars || 10)
    ).join(' | '));
  });

  return formatted.join('\n');
};

/**
 * Pad string to specific length
 */
const padString = (str, length) => {
  const s = String(str);
  if (s.length >= length) return s.substring(0, length);
  return s + ' '.repeat(length - s.length);
};

/**
 * Convert table to HTML
 */
export const tableToHTML = (table) => {
  if (!table) return '';

  const { headers, rows, columnTypes } = table;
  const dataRows = rows.map(r => Array.isArray(r) ? r : r.data || []);

  let html = '<table class="parsed-table">';
  
  // Add headers
  if (headers) {
    html += '<thead><tr>';
    headers.forEach((header, i) => {
      const type = columnTypes?.[i] || 'text';
      html += `<th class="col-${type}">${escapeHTML(header)}</th>`;
    });
    html += '</tr></thead>';
  }

  // Add body
  html += '<tbody>';
  dataRows.forEach((row, rowIndex) => {
    const highlight = rows[rowIndex]?.highlight;
    html += `<tr${highlight ? ` class="row-${highlight}"` : ''}>`;
    row.forEach((cell, i) => {
      const type = columnTypes?.[i] || 'text';
      html += `<td class="col-${type}">${formatCellValue(cell, type)}</td>`;
    });
    html += '</tr>';
  });
  html += '</tbody></table>';

  return html;
};

/**
 * Format cell value based on type
 */
const formatCellValue = (value, type) => {
  if (!value) return '';

  const escaped = escapeHTML(String(value));

  switch (type) {
    case 'number':
      return `<span class="numeric">${escaped}</span>`;
    case 'currency':
      return `<span class="currency">${escaped}</span>`;
    case 'percentage':
      return `<span class="percentage">${escaped}</span>`;
    case 'date':
      return `<span class="date">${escaped}</span>`;
    case 'boolean':
      const isTrue = /yes|true|✓/i.test(value);
      return `<span class="boolean ${isTrue ? 'true' : 'false'}">${escaped}</span>`;
    case 'email':
      return `<a href="mailto:${escaped}" class="email">${escaped}</a>`;
    case 'url':
      if (/^https?:\/\//i.test(value)) {
        return `<a href="${escaped}" target="_blank" rel="noopener noreferrer" class="url">${escaped}</a>`;
      }
      return escaped;
    default:
      return escaped;
  }
};

/**
 * Escape HTML special characters
 */
const escapeHTML = (str) => {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
};